// URL-slug helpers for the static Related News listing pages (WEB-27455).
//
// content-hub serves every page flat from /institution-profiles/ with a relative
// assetPrefix ("."), so pages MUST stay at depth-1. Each paginated Related News
// page is therefore its own flat slug alongside the profile slug:
//   profile:        bain-capital.html
//   related news 1: bain-capital-related-news.html
//   related news 2: bain-capital-related-news-page-2.html
// Each page is an independently crawlable static file (one merged "related
// stories" list per firm; about + mention combined).

export const RELATED_NEWS_PAGE_SIZE = 10;
const SUFFIX = "related-news";

// Build the listing slug for a 1-based page. Page 1 omits the -page-N tail.
export const buildRelatedNewsSlug = (institutionSlug: string, page = 1): string =>
  page > 1 ? `${institutionSlug}-${SUFFIX}-page-${page}` : `${institutionSlug}-${SUFFIX}`;

const SLUG_RE = new RegExp(`^(.*)-${SUFFIX}(?:-page-(\\d+))?$`);

// If `slug` is a Related News listing slug, return the underlying profile slug and
// 1-based page; otherwise null. Caller must still confirm baseSlug is a real profile.
export const parseRelatedNewsSlug = (slug: string): { baseSlug: string; page: number } | null => {
  const m = slug.match(SLUG_RE);
  if (!m || !m[1]) return null;
  const page = m[2] ? parseInt(m[2], 10) : 1;
  if (!Number.isFinite(page) || page < 1) return null;
  return { baseSlug: m[1], page };
};

export const relatedNewsPageCount = (totalItems: number): number =>
  Math.max(1, Math.ceil(totalItems / RELATED_NEWS_PAGE_SIZE));

// Carve one 1-based page out of the already-filtered set. Pages are cut locally
// because RA API's pagination.total counts hits BEFORE it drops cross-published
// records, so its page windows come back short (WEB-27455).
export const sliceRelatedNewsPage = <T>(items: readonly T[], page: number): T[] =>
  items.slice((page - 1) * RELATED_NEWS_PAGE_SIZE, page * RELATED_NEWS_PAGE_SIZE);

// Split the About bucket into the Featured (overview panel) article and the
// "Stories about" grid (WEB-32715, AC4).
//
// The Featured slot takes the newest About article whenever one exists. Before
// WEB-32715 a lone About article was deliberately left in the grid with the panel
// empty; AC4 requires the panel populated from a single eligible association, so the
// grid now hides itself instead (via the existing empty-section guards).
//
// slice(1, ...) is what keeps the Featured article out of the grid, so the two can
// never contain the same record. ABOUT_SECTION_LIMIT is 8 rather than 9 because the
// ninth About record is the Featured one.
export const ABOUT_SECTION_LIMIT = 8;

export const splitAboutItems = <T>(
  aboutItems: readonly T[]
): { firstAboutArticle: T | null; aboutItemsProp: T[] } => ({
  firstAboutArticle: aboutItems.length > 0 ? aboutItems[0] : null,
  aboutItemsProp: aboutItems.slice(1, ABOUT_SECTION_LIMIT + 1),
});
